import { Controller } from "@hotwired/stimulus"

// Scarica un file con fetch e lo salva col nome dato dal server (downloadFile di lib/api.js dell'originale):
// nome da Content-Disposition (filename* poi filename), altrimenti `nome`. Se non riesce: toast.error(`errore`) e la
// causa in console; con `silenzioso` niente di tutto questo, come `.catch(() => {})` dei pulsanti del Layout.
// Scarica solo dalla stessa origine della pagina (la sessione a cookie non va mandata altrove): un indirizzo vuoto o di
// un'altra origine è un errore, senza richiesta.
// Con `form` (id di un form) aggiunge all'indirizzo i campi compilati di quel form (i filtri scritti, come query()
// dell'originale); senza, l'indirizzo resta com'è.
// Con i target `icona` e `attesa` (Organizzazioni.js: `exporting`) durante lo scaricamento il pulsante è disattivato
// e l'icona lascia il posto al Loader2 che gira; senza, il pulsante non cambia.
// Con `nomeFisso` il file si salva sempre come `nome` (exportPdf di ProjectDetail.js: a.download col nome del progetto,
// senza leggere Content-Disposition); con `successo` il toast.success alla fine («Piano di finanziamento esportato»).
// Con `ext` (download di ApplicationPrep.js, i file della domanda) il nome si calcola qui, senza Content-Disposition:
// `nome` (o «documento») senza i caratteri vietati nei nomi dei file e senza l'estensione da documento, poi «.{ext}».
// Con `avvisoAi` le proposte dell'AI non confermate (intestazione x-campi-da-confermare, altrimenti `daConfermare`)
// diventano il toast.warning prima di salvare il file; l'errore della domanda è `errore` = «Download non riuscito.».
// Con `avvisoPrima` (downloadZip di ApplicationPrep.js: le proposte dell'AI da confermare della versione attuale) il
// testo è un toast.warning subito, prima della richiesta e qualunque sia l'esito.
// Con `inline` (openDoc di BandoDocuments.js) il file non si salva: si apre in una nuova scheda (window.open del blob con
// "noopener", indirizzo revocato dopo 60 s); l'errore è `errore` = «Impossibile aprire il documento.».
// Con `inline` un errore dell'API in JSON con `detail` (Pagamenti.js: apriDocumento) è il toast.error con quel testo; senza `detail`, `errore`.
// Con `senzaDettaglio` (openDoc di BandoDocuments.js: catch → sempre «Impossibile aprire il documento.») il `detail` non si usa mai.
// <button data-controller="scarica" data-action="scarica#scarica" data-scarica-url-value="/api/…"
//         data-scarica-nome-value="file.csv" data-scarica-errore-value="Esportazione non riuscita">
export default class extends Controller {
  static targets = ["icona", "attesa"]
  static values = {
    url: String,
    nome: { type: String, default: "download" },
    errore: { type: String, default: "Esportazione non riuscita, riprova." },
    form: String,
    silenzioso: Boolean,
    nomeFisso: Boolean,
    successo: String,
    ext: String,
    avvisoAi: Boolean,
    daConfermare: Number,
    inline: Boolean,
    senzaDettaglio: Boolean,
    avvisoPrima: String,
  }

  async scarica(event) {
    event.preventDefault()
    this.occupato(true)
    if (this.avvisoPrimaValue) window.toast?.warning?.(this.avvisoPrimaValue)
    try {
      const res = await fetch(this.indirizzo(), { credentials: "same-origin" })
      if (!res.ok) {
        // inline: l'errore dell'API è JSON con `detail` (openDoc di Pagamenti.js: JSON.parse(await e.response.data.text()).detail)
        if (this.inlineValue && !this.senzaDettaglioValue) {
          let detail = ""
          try { detail = (await res.json()).detail } catch { /* non JSON: il testo `errore` */ }
          if (typeof detail === "string" && detail) { window.toast?.error?.(detail); return }
        }
        throw new Error(`HTTP ${res.status}`)
      }
      if (this.inlineValue) {
        const blobUrl = URL.createObjectURL(await res.blob())
        window.open(blobUrl, "_blank", "noopener")
        setTimeout(() => URL.revokeObjectURL(blobUrl), 60000)
        return
      }
      const nome = this.extValue ? nomeConEstensione(this.hasNomeValue ? this.nomeValue : "", this.extValue)
        : (!this.nomeFissoValue && nomeDaIntestazione(res.headers.get("content-disposition"))) || this.nomeValue
      const href = URL.createObjectURL(await res.blob())
      if (this.avvisoAiValue) this.avvisaDaConfermare(res.headers.get("x-campi-da-confermare"))
      const a = document.createElement("a")
      a.href = href
      a.download = nome
      document.body.appendChild(a)
      a.click()
      a.remove()
      setTimeout(() => URL.revokeObjectURL(href), 1000)
      if (this.successoValue) window.toast?.success?.(this.successoValue)
    } catch (e) {
      if (this.silenziosoValue) return
      console.error("Download non riuscito", e)
      window.toast?.error?.(this.erroreValue || "Esportazione non riuscita, riprova.")
    } finally {
      this.occupato(false)
    }
  }

  // the AI's proposals not confirmed are highlighted in the file and said here (titolare, 11/10/2026)
  avvisaDaConfermare(intestazione) {
    const n = Number(intestazione ?? this.daConfermareValue) || 0
    if (n > 0) window.toast?.warning?.(`Ci sono ${n} campi da confermare: nel file c'è «[DA CONFERMARE IN EROGO]» al loro posto: confermale qui.`)
  }

  // setExporting: solo i pulsanti con il Loader2 (target `attesa`) si disattivano e lo mostrano al posto dell'icona.
  occupato(si) {
    if (!this.hasAttesaTarget) return
    this.element.disabled = si
    this.attesaTargets.forEach((el) => el.classList.toggle("hidden", !si))
    this.iconaTargets.forEach((el) => el.classList.toggle("hidden", si))
  }

  // L'indirizzo assoluto da scaricare; solleva se è vuoto o di un'altra origine.
  indirizzo() {
    if (!this.urlValue.trim()) throw new Error("Indirizzo del download mancante")
    const url = new URL(this.urlValue, window.location.href)
    if (url.origin !== window.location.origin) throw new Error(`Download da un'altra origine rifiutato: ${url.origin}`)
    const form = this.formValue && document.getElementById(this.formValue)
    if (form) new FormData(form).forEach((v, k) => { if (typeof v === "string" && v) url.searchParams.set(k, v) })
    return url.href
  }
}

// nomeDaIntestazione di lib/api.js, più robusto (stessi risultati per le intestazioni normali): `filename*` con
// charset UTF-8 senza distinzione di maiuscole e con la lingua (`utf-8'it'…`), `filename` solo come parola intera,
// nome tra virgolette anche con `;` e caratteri con escape.
export function nomeDaIntestazione(cd) {
  const s = cd || ""
  const star = s.match(/(?:^|[;\s])filename\*\s*=\s*([\w-]+)'[^']*'([^;]+)/i)
  if (star && star[1].toLowerCase() === "utf-8") {
    try { return decodeURIComponent(star[2].trim()) } catch { /* non codificato: il nome semplice qui sotto */ }
  }
  const quoted = s.match(/(?:^|[;\s])filename\s*=\s*"((?:[^"\\]|\\.)*)"/i)
  if (quoted) return quoted[1].replace(/\\(.)/g, "$1").trim()
  const m = s.match(/(?:^|[;\s])filename\s*=\s*([^;]+)/i)
  return m ? m[1].trim() : ""
}

// Il nome del file di un documento della domanda (download di ApplicationPrep.js): solo i caratteri che un nome di file
// non può avere diventano «_» (le lettere accentate restano, punto 12, 14/10/2026), via l'estensione da documento.
export function nomeConEstensione(nome, ext) {
  const base = (nome || "documento").replace(/[\\/:*?"<>|]+/g, "_").replace(/\.(pdf|docx?|rtf|odt)$/i, "")
  return `${base}.${ext || "pdf"}`
}
